import React from "react";
import { Link } from "react-router-dom";
import { useSeo, personJsonLd } from "@/lib/seo";
import { PageHeader, SectionLabel } from "@/components/shared";
import { IMAGES } from "@/lib/assets";
import { CheckCircle2, Shield, Lock, Scale, MapPin, Globe, Instagram } from "lucide-react";

const training = [
  "Family Systemic Constellation Training",
  "Clinical Hypnotherapy",
  "Pranic Healing",
  "Crystal Healing",
  "Transpersonal Regression",
  "Numerology",
];

export default function About() {
  useSeo({
    title: "About Arjunna Soni — Family Constellation Facilitator, Ludhiana",
    description: "Meet Arjunna Soni: a Family & Systemic Constellation facilitator in Ludhiana with 7+ years' practice, 1,000+ clients, and training across constellation work, hypnotherapy and more.",
    path: "/about",
    jsonLd: personJsonLd(),
  });
  return (
    <div>
      <PageHeader crumb="About" title="Meet Arjunna" />

      {/* INTRO */}
      <section className="container-page py-10 md:py-16">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="relative">
            <div className="absolute inset-0 translate-x-4 translate-y-4 border border-gold rounded-lg" />
            <img src={IMAGES.portrait} alt="Portrait of Arjunna Soni, Family Constellation facilitator in Ludhiana" width="900" height="1200" loading="eager" className="relative w-full aspect-[3/4] object-cover rounded-lg" />
          </div>
          <div className="text-lg text-earth/80 space-y-5">
            <p>
              My journey into this work wasn't a straight line, but a slow unfolding — driven by a deep curiosity about the
              unseen threads that bind a family together. I kept being drawn to the patterns that echo across generations:
              the unspoken loyalties that shape our lives long before we're aware of them.
            </p>
            <blockquote className="py-6 border-y hairline my-6 text-center">
              <p className="text-2xl italic text-gold-dark font-display leading-snug">
                "Healing is rarely about fixing what is broken; it is about gently illuminating what has been hidden."
              </p>
            </blockquote>
            <p>
              Discovering Family Constellations was a turning point. It offered a lens that honours the whole web of
              relationships and inherited narratives a person belongs to. My practice is rooted in this systemic
              perspective, blending intuitive sensitivity with rigorous training.
            </p>
            <p>
              I believe in the innate movement toward resolution that exists within every family system. My role is simply
              to hold a safe, clear space for that movement to happen — so entanglements can loosen, and love can flow again.
            </p>
          </div>
        </div>
      </section>

      {/* TRAINING */}
      <section className="bg-surface-card border-y hairline py-16 md:py-24">
        <div className="container-page grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionLabel>Lineage & learning</SectionLabel>
            <h2 className="text-3xl md:text-4xl text-earth mb-4" style={{ fontWeight: 600 }}>Trained across disciplines</h2>
            <p className="text-lg text-earth/70">
              Facilitation asks for more than intuition — it needs a foundation of rigorous training and deep respect for
              the method. My practice draws on extensive study across several complementary disciplines.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-4">
              {training.map((t) => (
                <li key={t} className="flex items-center gap-3 text-lg text-earth border-b hairline pb-4 last:border-0">
                  <CheckCircle2 className="text-gold shrink-0" size={20} /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NUMBERS */}
      <section className="container-page py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 text-center divide-y md:divide-y-0 md:divide-x divide-hairline">
          {[["7+", "Years of practice"], ["1,000+", "Clients served"], ["Both", "Individual & group formats"]].map(([v, l]) => (
            <div key={l} className="p-8">
              <div className="text-4xl md:text-6xl text-gold-dark font-display mb-3" style={{ fontWeight: 600 }}>{v}</div>
              <div className="label-caps text-earth/60">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW I WORK */}
      <section className="bg-surface-card border-y hairline py-16 md:py-24">
        <div className="container-page grid md:grid-cols-2 gap-12">
          <div>
            <SectionLabel>How I work</SectionLabel>
            <h2 className="text-3xl md:text-4xl text-earth mb-5" style={{ fontWeight: 600 }}>Grounded, and never mystical</h2>
            <p className="text-lg text-earth/70 mb-5">
              My approach is deliberately practical and compassionate. I view systemic work as a tool for uncovering the
              hidden dynamics that shape our daily lives, relationships, and wellbeing.
            </p>
            <p className="text-lg text-earth/70">
              I aim to demystify the process — to create a space that feels accessible and safe. The focus is always
              clarity: bringing unconscious patterns into awareness so genuine movement can happen.
            </p>
          </div>
          <div className="flex flex-col justify-center gap-6">
            {[[Shield, "A safe space"], [Lock, "Complete confidentiality"], [Scale, "Professional integrity"]].map(([Icon, label], i) => (
              <div key={i}>
                <div className="flex items-center gap-4">
                  <Icon className="text-earth" size={30} strokeWidth={1.5} />
                  <div className="text-xl text-earth font-display">{label}</div>
                </div>
                {i < 2 && <div className="border-b hairline mt-6" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHERE */}
      <section className="container-page py-16 md:py-24">
        <div className="bg-surface-card border hairline rounded-lg p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <SectionLabel>Where I practise</SectionLabel>
            <h2 className="text-3xl text-earth mb-5" style={{ fontWeight: 600 }}>In Ludhiana & online</h2>
            <div className="space-y-2 text-earth/80">
              <p className="font-semibold text-earth flex items-center gap-2"><MapPin size={18} className="text-gold" /> Holistic Healing and Wellness</p>
              <p>81/7 Mall Road, Opp Sutluj Club Main Gate, Civil Lines, Ludhiana (Pb)-141001</p>
              <p>+91 9115423336</p>
              <a href="https://instagram.com/arjunnasoni" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-gold-dark"><Instagram size={16} /> @arjunnasoni</a>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-earth/70 bg-parchment border hairline rounded-md px-4 py-3">
              <Globe size={18} className="text-gold" /> Online sessions available worldwide via Zoom.
            </div>
          </div>
          <img src={IMAGES.interior} alt="The Family Constellation practice space in Ludhiana" width="1200" height="900" loading="lazy" className="w-full aspect-[4/3] object-cover rounded-lg" />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-earth text-parchment py-20 text-center">
        <div className="container-page max-w-2xl">
          <h2 className="text-3xl md:text-5xl text-parchment" style={{ fontWeight: 600 }}>Begin your journey toward resolution.</h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-gold">Book a Session</Link>
            <Link to="/testimonials" className="inline-flex items-center justify-center border border-parchment text-parchment font-bold px-6 py-3 rounded-md hover:bg-parchment hover:text-earth transition-all">Read client stories</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
